import type { LessonDetail } from '@skolveckan/shared';
import { IconBook, IconLink } from './Icons';

interface Props {
  lesson: LessonDetail;
  /** Kompakt används i adminvyns förhandsvisning. */
  compact?: boolean;
}

/**
 * Samma komponent renderar elevens modal och lärarens förhandsvisning. Det är
 * poängen: två implementationer skulle glida isär, och då är förhandsvisningen
 * värdelös. Se docs/DESIGN.md § Komponentinventering.
 */
export function LessonContent({ lesson, compact }: Props) {
  const hasContent =
    Boolean(lesson.title) || lesson.steps.length > 0 || Boolean(lesson.book.title);

  if (!hasContent) {
    return (
      <div className="lessoncontent">
        <p className="lessoncontent__empty">
          Läraren har inte lagt in något innehåll för den här lektionen än.
        </p>
      </div>
    );
  }

  return (
    <div className={`lessoncontent${compact ? ' lessoncontent--compact' : ''}`}>
      {!compact && <span className="label">Det här ska du göra</span>}
      {lesson.title && <h3 className="lessoncontent__title">{lesson.title}</h3>}

      {lesson.steps.length > 0 && (
        <ol className="steps">
          {lesson.steps.map((step, i) => (
            <li key={step.id} className="steps__item">
              <span className="steps__num">{i + 1}</span>
              <span className="steps__text">{step.text}</span>
            </li>
          ))}
        </ol>
      )}

      {(lesson.book.title || lesson.book.pages || lesson.book.exercises) && (
        <div className="bookbox">
          <span className="bookbox__icon" aria-hidden="true">
            <IconBook />
          </span>
          <span className="stack">
            <span className="label">I boken</span>
            <span className="bookbox__title">
              {[lesson.book.title, lesson.book.pages ? `s. ${lesson.book.pages}` : null]
                .filter(Boolean)
                .join(', ')}
            </span>
            {lesson.book.exercises && (
              <span className="bookbox__exercises">{lesson.book.exercises}</span>
            )}
          </span>
        </div>
      )}

      {lesson.helps.length > 0 && (
        <div className="stack" style={{ gap: 8 }}>
          {!compact && <span className="label">Hjälpavsnitt</span>}
          <div className="chiprow">
            {lesson.helps.map((help) => (
              <span key={help.id} className="chip">
                {help.text}
              </span>
            ))}
          </div>
        </div>
      )}

      {lesson.links.length > 0 && (
        <div className="stack" style={{ gap: 7 }}>
          {!compact && <span className="label">Hjälp på nätet</span>}
          {lesson.links.map((link) => (
            <a
              key={link.id}
              className="linkrow"
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="linkrow__icon" aria-hidden="true">
                <IconLink />
              </span>
              <span className="stack">
                <span className="linkrow__title">{link.title}</span>
                {link.sourceLabel && <span className="linkrow__source">{link.sourceLabel}</span>}
              </span>
            </a>
          ))}
        </div>
      )}

      {lesson.handInNote && (
        <div className="handin">
          <span>{lesson.handInNote}</span>
        </div>
      )}
    </div>
  );
}
